// A subagent's data (Settings → What it can use → Data): the connected data
// sources it holds — its OWN ones (given here: `own`) and those a workflow
// or a job gives it (`through`) — and the reference files kept in its
// workspace (`reference/`), all on every run. PUT …/sources sets only the
// own ones. Every change is saved at once (its own routes, not the Settings
// Save) and re-renders the subagent's brief. Routes: routes/subagent_data.py;
// refusals are `{detail: {code, message}}` (workflowError words them).
//
// Its connections (a workspace with Connections on): GET/PUT
// …/data/connections, the same split (`own` given here, `through` a
// workflow or a job). The types are written here until api.gen.ts has them;
// a workspace without Connections answers 409 `connections_disabled`, read
// as null (nothing to show), never as an error.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch, errorCode } from "@/lib/api";
import type { components } from "@/types/api.gen";

import { connectionsKey } from "./connections";
import { dataSourceKeys } from "./dataSources";

type RawView = components["schemas"]["SubagentDataView"];
type RawSource = components["schemas"]["SubagentDataSource"];
type RawFile = components["schemas"]["SubagentDataFile"];
type RawGrantFrom = components["schemas"]["DataGrantFrom"];

/** A workflow or a job that gives the subagent a source. */
export type DataGrantFrom = RawGrantFrom & Required<Pick<RawGrantFrom, "label">>;

/** The server's view with its defaults filled in (the generated types make every defaulted field optional). */
export type SubagentDataSource = Omit<RawSource, "through"> &
  Required<Pick<RawSource, "status" | "summary" | "tables" | "workflows" | "own" | "via">> & { through: DataGrantFrom[] };
export type SubagentDataFile = RawFile & Required<Pick<RawFile, "modified" | "kind">>;
export type SubagentDataView = Omit<RawView, "sources" | "available" | "files"> & {
  sources: SubagentDataSource[];
  available: SubagentDataSource[];
  files: SubagentDataFile[];
  total_bytes: number;
  can_query: boolean;
  folder: string;
};

const source = (s: RawSource): SubagentDataSource => ({
  ...s,
  status: s.status ?? "new",
  summary: s.summary ?? "",
  tables: s.tables ?? 0,
  workflows: s.workflows ?? [],
  own: s.own ?? true,
  via: s.via ?? [],
  through: (s.through ?? []).map((t) => ({ ...t, label: t.label ?? "" })),
});

/** Fill the defaults in once, where a response arrives. */
export function normalizeSubagentData(raw: RawView): SubagentDataView {
  return {
    ...raw,
    sources: (raw.sources ?? []).map(source),
    available: (raw.available ?? []).map(source),
    files: (raw.files ?? []).map((f) => ({ ...f, modified: f.modified ?? "", kind: f.kind ?? "text" })),
    total_bytes: raw.total_bytes ?? 0,
    can_query: raw.can_query ?? true,
    folder: raw.folder ?? "",
  };
}

const base = (c: string, t: string) => `/api/customers/${encodeURIComponent(c)}/subagent-blueprints/${encodeURIComponent(t)}/data`;

export const subagentDataKeys = {
  one: (c: string | null, t: string | null) => ["subagent-data", c, t] as const,
  connections: (c: string | null, t: string | null) => ["subagent-data", c, t, "connections"] as const,
};

/** Its sources (and the workspace's others it could get) and its files; re-read every 2 s while a source of its discovers. */
export function useSubagentData(customerId: string | null, template: string | null) {
  return useQuery({
    queryKey: subagentDataKeys.one(customerId, template),
    enabled: !!customerId && !!template,
    queryFn: async ({ signal }) => normalizeSubagentData(await apiFetch<RawView>(base(customerId!, template!), { signal })),
    staleTime: 5_000,
    refetchInterval: (q) => ((q.state.data?.sources ?? []).some((s) => s.status === "discovering") ? 2_000 : false),
  });
}

function saved(qc: QueryClient, customerId: string, template: string, view: SubagentDataView) {
  qc.setQueryData(subagentDataKeys.one(customerId, template), view);
  // A source's scope changed: the Data page lists who may use it.
  void qc.invalidateQueries({ queryKey: dataSourceKeys.all(customerId) });
}

/** Give it exactly these sources (added to each one's scope, taken out of every other). */
export function useSetSubagentDataSources() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, template, names }: { customerId: string; template: string; names: string[] }) =>
      normalizeSubagentData(await apiFetch<RawView>(`${base(customerId, template)}/sources`, { method: "PUT", body: { names } })),
    onSuccess: (view, vars) => saved(qc, vars.customerId, vars.template, view),
  });
}

/** One reference file (multipart `file`) with upload progress (0..1): an XHR, since fetch cannot report it. */
export function uploadSubagentDataFile(
  { customerId, template, file }: { customerId: string; template: string; file: File },
  onProgress?: (fraction: number) => void,
): Promise<SubagentDataView> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${base(customerId, template)}/files`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, "The upload did not reach the server.", null));
    xhr.onload = () => {
      let parsed: unknown = null;
      if (xhr.responseText) {
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          parsed = { error: "non_json_response", snippet: xhr.responseText.slice(0, 400) };
        }
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(normalizeSubagentData(parsed as RawView));
      else reject(new ApiError(xhr.status, `upload failed: ${xhr.status}`, parsed));
    };
    const form = new FormData();
    form.append("file", file, file.name);
    xhr.send(form);
  });
}

export function useUploadSubagentDataFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { customerId: string; template: string; file: File; onProgress?: (fraction: number) => void }) =>
      uploadSubagentDataFile(vars, vars.onProgress),
    onSuccess: (view, vars) => saved(qc, vars.customerId, vars.template, view),
  });
}

export function useDeleteSubagentDataFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, template, name }: { customerId: string; template: string; name: string }) =>
      normalizeSubagentData(await apiFetch<RawView>(`${base(customerId, template)}/files/${encodeURIComponent(name)}`, { method: "DELETE" })),
    onSuccess: (view, vars) => saved(qc, vars.customerId, vars.template, view),
  });
}

// ─── its connections (Connections on) ───

/** A connection it holds (`own`: given here) or could be given (`available`); `through`: the workflows and jobs that give it. */
export interface SubagentConnection {
  id: string;
  name: string;
  connector: string;
  status: string;
  purpose: string;
  own: boolean;
  via: string[];
  through: DataGrantFrom[];
}

export interface SubagentConnectionsView {
  template: string;
  connections: SubagentConnection[];
  available: SubagentConnection[];
  /** false: its Integrations switch is off, so a connection given here does nothing yet. */
  integrations_on: boolean;
}

type RawConnection = Partial<Omit<SubagentConnection, "through">> & { id: string; through?: RawGrantFrom[] };
type RawConnectionsView = { template: string; connections?: RawConnection[]; available?: RawConnection[]; integrations_on?: boolean };

const connection = (c: RawConnection): SubagentConnection => ({
  id: c.id,
  name: c.name || c.id,
  connector: c.connector ?? "",
  status: c.status ?? "ok",
  purpose: c.purpose ?? "",
  own: c.own ?? true,
  via: c.via ?? [],
  through: (c.through ?? []).map((t) => ({ ...t, label: t.label ?? "" })),
});

export function normalizeSubagentConnections(raw: RawConnectionsView): SubagentConnectionsView {
  return {
    template: raw.template,
    connections: (raw.connections ?? []).map(connection),
    available: (raw.available ?? []).map(connection),
    integrations_on: raw.integrations_on ?? true,
  };
}

/** The workspace is not on Connections: the subagent routes answer 409 `connections_disabled` (`{detail: {code}}`). */
function connectionsOff(e: unknown): boolean {
  if (!(e instanceof ApiError) || e.status !== 409) return false;
  const detail = (e.body as { detail?: { code?: unknown } } | null)?.detail;
  return detail?.code === "connections_disabled" || errorCode(e) === "connections_disabled";
}

/** The query of its connections (the hook's, and a save's read when the hook has not loaded). */
export function subagentConnectionsQuery(customerId: string, template: string) {
  return {
    queryKey: subagentDataKeys.connections(customerId, template),
    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<SubagentConnectionsView | null> => {
      try {
        return normalizeSubagentConnections(await apiFetch<RawConnectionsView>(`${base(customerId, template)}/connections`, { signal }));
      } catch (e) {
        if (connectionsOff(e)) return null;
        throw e;
      }
    },
    staleTime: 5_000,
  };
}

/** Its connections and the ones it could get; null on a workspace without Connections. */
export function useSubagentConnections(customerId: string | null, template: string | null) {
  return useQuery({ ...subagentConnectionsQuery(customerId ?? "", template ?? ""), enabled: !!customerId && !!template });
}

/** Give it exactly these connections as its own (what a workflow or a job gives it stays). */
export function useSetSubagentConnections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, template, names }: { customerId: string; template: string; names: string[] }) =>
      normalizeSubagentConnections(await apiFetch<RawConnectionsView>(`${base(customerId, template)}/connections`, { method: "PUT", body: { names } })),
    onSuccess: (view, vars) => {
      qc.setQueryData(subagentDataKeys.connections(vars.customerId, vars.template), view);
      // Who may use each connection changed: the Connections page and its data view read it.
      void qc.invalidateQueries({ queryKey: connectionsKey(vars.customerId) });
      void qc.invalidateQueries({ queryKey: subagentDataKeys.one(vars.customerId, vars.template), exact: true });
    },
  });
}
